import React, { useState } from 'react';
import { Moon, ChevronDown, Loader2, User } from 'lucide-react';
import { cn } from '../../utils/cn';
import SidebarToggle from './SidebarToggle';
import GlobalSearch from '../search/GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import LanguageSelector from '../common/LanguageSelector';

const Header = () => {
  const { user, logout, loading } = useAuth();
  const { organization } = useOrganization();
  const { t } = useLanguage();
  const [showProfile, setShowProfile] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Handle logout when the user clicks Sign Out
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      // The navigation will be handled by the logout function
    } catch (error) {
      console.error('Error logging out:', error);
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="sticky top-0 z-30 h-16 w-full bg-white/80 backdrop-blur-xl border-b">
      <div className="h-full px-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <SidebarToggle />
          <GlobalSearch />
        </div>
        
        <div className="flex items-center gap-2">
          <LanguageSelector />
          
          <button 
            className="p-2 rounded-lg transition-colors cursor-not-allowed opacity-70 relative group"
            onClick={(e) => e.preventDefault()}
          >
            <Moon className="w-5 h-5 text-slate-600" />
            <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 text-xs font-medium bg-slate-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
              {t('common.darkModeSoon')}
            </span>
          </button>

          <div className="relative">
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center gap-3 p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              {organization?.logo ? (
                <img
                  src={organization.logo}
                  alt="Organization logo"
                  className="w-8 h-8 rounded-lg object-contain bg-white"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
              )}
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-slate-900 leading-tight">
                  {user ? user.username : 'Guest User'}
                </p>
                <p className="text-xs text-slate-500 leading-tight">
                  {user ? 'Logged In' : 'Not Logged In'}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-500" />
            </button>

            {showProfile && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border py-2">
                <button 
                  onClick={handleLogout}
                  disabled={isLoggingOut || loading}
                  className="w-full px-4 py-2 text-left text-red-600 hover:bg-slate-50 transition-colors flex items-center"
                >
                  {isLoggingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('common.signingOut')}
                    </>
                  ) : (
                    t('common.signOut')
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;